import { Plus, Trash2 } from "lucide-react";
import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea, Pills, RAG_OPTIONS, TextField } from "@/components/tools/fields";
import { RowsEditor, blankRows } from "@/components/tools/blocks";

const TEXT = [
  { k: "priority", label: "Priority" },
  { k: "current", label: "Current position" },
  { k: "outcome", label: "Desired outcome" },
];
const ACTION_COLS = [
  { k: "action", label: "Action" },
  { k: "lead", label: "Lead" },
  { k: "deadline", label: "Deadline", type: "date" },
];
const RAG = RAG_OPTIONS.map((o) => ({ ...o, hint: undefined }));
const newPlan = (id) => ({ id, actions: blankRows(2) });
const INIT = { plans: [newPlan("p1"), newPlan("p2"), newPlan("p3")] };

const Plan = ({ plan, i, onChange, onRemove }) => {
  const set = (k) => (v) => onChange({ ...plan, [k]: v });
  return (
    <li className="avoid-break rounded-2xl border-2 border-line bg-surface p-5 sm:p-6" data-testid={`dp-plan-${i}`}>
      <div className="mb-4 flex items-center justify-between">
        <h4 className="font-display text-xl font-medium">Priority area {i + 1}</h4>
        <button type="button" onClick={onRemove} aria-label={`Remove priority area ${i + 1}`} data-testid={`dp-remove-${i}`}
          className="no-print flex h-10 w-10 items-center justify-center rounded-full border-2 border-line hover:border-ink"><Trash2 size={15} aria-hidden /></button>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {TEXT.map((f) => <AutoTextarea key={f.k} id={`dp-${plan.id}-${f.k}`} label={f.label} rows={2} value={plan[f.k]} onChange={set(f.k)} testid={`dp-${i}-${f.k}`} />)}
      </div>
      <h5 className="mb-3 mt-6 font-bold">Actions required</h5>
      <RowsEditor name={`dp-${i}-actions`} rows={plan.actions} setRows={set("actions")} cols={ACTION_COLS} addLabel="Add an action" grid="sm:grid-cols-3" />
      <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,1.4fr)_auto_200px] md:items-start">
        <AutoTextarea id={`dp-${plan.id}-success`} label="Success measures" rows={2} value={plan.success} onChange={set("success")} testid={`dp-${i}-success`} />
        <Pills legend="RAG status" hideLegend={false} name={`dp-${plan.id}-rag`} options={RAG} value={plan.rag} onChange={set("rag")} testid={`dp-${i}-rag`} />
        <TextField id={`dp-${plan.id}-review`} label="Review date" type="date" value={plan.review} onChange={set("review")} testid={`dp-${i}-review`} />
      </div>
    </li>
  );
};

export const ActionPlan = () => {
  const [data, setData, reset] = useStoredState("action-plan-v3", INIT);
  const plans = Array.isArray(data.plans) ? data.plans : [];
  return (
    <ToolShell id="action-plan" onReset={reset}>
      <ol className="space-y-5">
        {plans.map((p, i) => (
          <Plan key={p.id} plan={p} i={i}
            onChange={(np) => setData({ plans: plans.map((x) => (x.id === p.id ? np : x)) })}
            onRemove={() => setData({ plans: plans.filter((x) => x.id !== p.id) })} />
        ))}
      </ol>
      <button type="button" className="btn btn-ghost btn-sm no-print mt-4" data-testid="dp-add"
        onClick={() => setData({ plans: [...plans, newPlan(`p${Date.now()}`)] })}>
        <Plus size={14} aria-hidden /> Add another priority area
      </button>
    </ToolShell>
  );
};
